import { useQueryClient } from "@tanstack/react-query";
import type { Editor, JSONContent } from "@tiptap/react";
import { AlertTriangle, Loader2, RefreshCw, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";

import {
  mineApi,
  mineQueryKeys,
  uploadToSignedUrl,
  useCreateTextItemMutation,
} from "@/modules/mine";
import { FolderSelector } from "@/modules/mine/components/folder-selector";
import { TagSelector } from "@/modules/mine/components/tag-selector";
import { TextPreviewLoading } from "@/modules/mine/components/text-preview-loading";
import { computeContentHash } from "@/modules/mine/content-hash";
import type { RouteInfo } from "@/modules/mine/navigation/types";
import {
  buildTextContentFileName,
  dehydrateAttachmentUrls,
  getTextContentMimeType,
  getTextContentMode,
  hydrateAttachmentUrls,
  parseTiptapJson,
  resolveInitialTextContentMode,
  selectMarkdownSource,
  type TextContentMode,
  type TextEditorContent,
} from "@/modules/mine/text-content";
import type { Item, ItemTagData } from "@/modules/mine/types";
import { isRequestCancellation } from "@/shared/api/request-cancellation";
import { NotionEditor, type UploadImageOptions } from "@/shared/components/editor";
import { repairNestedMarkdownImageLinks } from "@/shared/components/editor/lib/markdown-image";
import { Button } from "@/shared/ui/button";

type AddTextResult = {
  action: "created" | "updated";
  folderId: string | null;
};

interface AddTextProps {
  initialContentMode?: TextContentMode;
  item?: Item;
  routeInfo: RouteInfo;
  onCancel?: () => void;
  onCreated?: (result: AddTextResult) => void;
  setSubmitting?: (value: boolean) => void;
}

interface AddTextOverlayProps {
  initialContentMode?: TextContentMode;
  item?: Item;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  routeInfo: RouteInfo;
}

function getDefaultFolderId(routeInfo: RouteInfo) {
  return routeInfo.folderId ?? null;
}

function getDefaultTitle(content: string) {
  const firstLine = content
    .split("\n")
    .map((line) => line.trim())
    .find(Boolean);

  return firstLine?.slice(0, 60) ?? "未命名文本";
}

function normalizeTitle(title: string) {
  return title.trim().replace(/\s+/g, " ");
}

function getFileTitle(file: File) {
  return file.name.replace(/\.[^.]+$/, "") || "图片";
}

function getEmptyEditorJson() {
  return {
    type: "doc",
    content: [{ type: "paragraph" }],
  } satisfies JSONContent;
}

const TEXT_NOTE_PREVIEW_BLOCK_LIMIT = 5;

function buildTextNotePreview(content: JSONContent) {
  return JSON.stringify({
    ...content,
    content: content.content?.slice(0, TEXT_NOTE_PREVIEW_BLOCK_LIMIT) ?? [],
  });
}

function textToEditorJson(text: string): JSONContent {
  const paragraphs = text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);

  return {
    type: "doc",
    content: paragraphs.length > 0
      ? paragraphs.map((paragraph) => ({
          type: "paragraph",
          content: [{ type: "text", text: paragraph }],
        }))
      : [{ type: "paragraph" }],
  };
}

function getInitialContentMode(
  item: Item | undefined,
  requestedMode: TextContentMode = "tiptap-json",
): TextContentMode {
  try {
    return resolveInitialTextContentMode(item?.mime_type, requestedMode);
  } catch {
    return "tiptap-json";
  }
}

function getEditorFallbackContent(item: Item | undefined, mode: TextContentMode): TextEditorContent {
  if (mode === "markdown") {
    return item?.note ?? "";
  }

  return item ? textToEditorJson(item.note || "") : getEmptyEditorJson();
}

function formatEditorDate(value?: string) {
  const date = value ? new Date(value) : new Date();

  if (Number.isNaN(date.getTime())) {
    return value ?? "";
  }

  return new Intl.DateTimeFormat("zh-CN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

function getEditorCharacterCount(editor: Editor | null, fallbackText: string) {
  const characterCount = editor?.storage.characterCount as
    | { characters?: () => number }
    | undefined;

  return characterCount?.characters?.() ?? Array.from(fallbackText).length;
}

function buildEditorTextPayload(params: {
  attachmentIdByUrl: ReadonlyMap<string, string>;
  editor: Editor | null;
  markdownEdited: boolean;
  mode: TextContentMode;
  normalizedTitle: string;
  originalMarkdown: string;
}) {
  const {
    attachmentIdByUrl,
    editor,
    markdownEdited,
    mode,
    normalizedTitle,
    originalMarkdown,
  } = params;
  const editorText = editor?.getText().trim() ?? "";
  const editorContent = editor?.getJSON() ?? getEmptyEditorJson();
  const persistedEditorContent = mode === "tiptap-json"
    ? dehydrateAttachmentUrls(editorContent, attachmentIdByUrl)
    : editorContent;
  const content = mode === "markdown"
    ? selectMarkdownSource({
        edited: markdownEdited,
        normalized: editor?.getMarkdown() ?? "",
        original: originalMarkdown,
      })
    : JSON.stringify(persistedEditorContent, null, 2);

  return {
    content,
    note: buildTextNotePreview(persistedEditorContent),
    title: normalizedTitle || getDefaultTitle(editorText),
    words_count: getEditorCharacterCount(editor, editorText),
  };
}

export function AddText({
  initialContentMode = "tiptap-json",
  item,
  onCancel,
  onCreated,
  routeInfo,
  setSubmitting,
}: AddTextProps) {
  const [contentMode, setContentMode] = useState<TextContentMode>(() =>
    getInitialContentMode(item, initialContentMode)
  );
  const [title, setTitle] = useState(item?.title ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [contentLoadError, setContentLoadError] = useState<string | null>(null);
  const [contentLoadRevision, setContentLoadRevision] = useState(0);
  const [initialContent, setInitialContent] = useState<TextEditorContent>(() =>
    getEditorFallbackContent(item, getInitialContentMode(item, initialContentMode))
  );
  const attachmentIdByUrlRef = useRef(new Map<string, string>());
  const autoCreateStartedRef = useRef(false);
  const currentItemRef = useRef<Item | null>(item ?? null);
  const markdownEditedRef = useRef(false);
  const originalMarkdownRef = useRef("");
  const pendingTextItemRef = useRef<Promise<Item> | null>(null);
  const editorRef = useRef<Editor | null>(null);
  const titleRef = useRef<HTMLTextAreaElement | null>(null);
  const [tags, setTags] = useState<ItemTagData[]>(() => item?.tags ?? []);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(
    item?.folder_id ?? getDefaultFolderId(routeInfo),
  );
  const queryClient = useQueryClient();
  const createTextItemMutation = useCreateTextItemMutation();

  const createdAtLabel = useMemo(() => formatEditorDate(item?.created_at), [item?.created_at]);
  const normalizedTitle = useMemo(() => normalizeTitle(title), [title]);

  const handleEditorReady = useCallback((editor: Editor | null) => {
    editorRef.current = editor;
  }, []);

  const handleEditorChange = useCallback(() => {
    if (contentMode === "markdown") {
      markdownEditedRef.current = true;
    }
  }, [contentMode]);

  useEffect(() => {
    const titleElement = titleRef.current;

    if (!titleElement) {
      return;
    }

    titleElement.style.height = "0px";
    titleElement.style.height = `${titleElement.scrollHeight}px`;
  }, [title]);

  useEffect(() => {
    let nextMode: TextContentMode;

    try {
      nextMode = resolveInitialTextContentMode(item?.mime_type, initialContentMode);
      setContentLoadError(null);
    } catch (error) {
      nextMode = "tiptap-json";
      setContentLoadError(error instanceof Error ? error.message : "不支持的文本内容类型");
    }

    currentItemRef.current = item ?? null;
    attachmentIdByUrlRef.current.clear();
    markdownEditedRef.current = false;
    originalMarkdownRef.current = "";
    setContentMode(nextMode);
    setTitle(item?.title ?? "");
    setTags(item?.tags ?? []);
    setSelectedFolderId(item?.folder_id ?? routeInfo.folderId ?? null);
    setInitialContent(getEditorFallbackContent(item, nextMode));
  }, [initialContentMode, item, routeInfo.folderId]);

  useEffect(() => {
    if (!item?.id || !item.url) {
      return;
    }

    const controller = new AbortController();

    setContentLoadError(null);
    setIsContentLoading(true);

    mineApi.getItem(item.id, {
      signal: controller.signal,
      silent: true,
      skipCancelToken: true,
    })
      .then(async (latestItem) => {
        const latestMode = getTextContentMode(latestItem.mime_type);

        currentItemRef.current = latestItem;
        setContentMode(latestMode);
        setTitle(latestItem.title ?? "");
        setTags(latestItem.tags ?? []);
        setSelectedFolderId(latestItem.folder_id ?? routeInfo.folderId ?? null);

        if (!latestItem.url) {
          return {
            latestItem,
            mode: latestMode,
            rawContent: "",
            attachmentsResponse: { attachments: [] },
          };
        }

        const rawContentPromise = fetch(latestItem.url, { signal: controller.signal }).then((response) => {
          if (!response.ok) {
            throw new Error("文本内容加载失败");
          }

          return response.text();
        });
        const [rawContent, attachmentsResponse] = await Promise.all([
          rawContentPromise,
          latestMode === "tiptap-json"
            ? mineApi.getItemAttachments(latestItem.id)
            : Promise.resolve({ attachments: [] }),
        ]);

        return { latestItem, mode: latestMode, rawContent, attachmentsResponse };
      })
      .then(({ mode, rawContent, attachmentsResponse }) => {
        if (mode === "markdown") {
          const repairedMarkdown = repairNestedMarkdownImageLinks(rawContent);

          originalMarkdownRef.current = repairedMarkdown;
          markdownEditedRef.current = false;
          setInitialContent(repairedMarkdown);
          return;
        }

        const attachmentUrlMap = new Map(
          attachmentsResponse.attachments.map((attachment) => [
            attachment.id,
            attachment.download_url,
          ]),
        );
        attachmentIdByUrlRef.current = new Map(
          attachmentsResponse.attachments.map((attachment) => [
            attachment.download_url,
            attachment.id,
          ]),
        );
        const parsedContent = parseTiptapJson(rawContent);

        setInitialContent(hydrateAttachmentUrls(parsedContent, attachmentUrlMap));
      })
      .catch((error: unknown) => {
        if (isRequestCancellation(error, controller.signal)) {
          return;
        }

        setContentLoadError(
          error instanceof Error ? error.message : "文本正文加载失败，请重试",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsContentLoading(false);
        }
      });

    return () => controller.abort();
  }, [contentLoadRevision, item, routeInfo.folderId]);

  const ensureTextItem = useCallback(async () => {
    const existingItem = currentItemRef.current;

    if (existingItem?.id) {
      return existingItem;
    }

    if (pendingTextItemRef.current) {
      return pendingTextItemRef.current;
    }

    const pendingTextItem = createTextItemMutation.mutateAsync({
      folder_id: selectedFolderId ?? undefined,
    });

    pendingTextItemRef.current = pendingTextItem;

    try {
      const createdItem = await pendingTextItem;
      currentItemRef.current = createdItem;
      return createdItem;
    } finally {
      pendingTextItemRef.current = null;
    }
  }, [createTextItemMutation, selectedFolderId]);

  useEffect(() => {
    if (item?.id || autoCreateStartedRef.current) {
      return;
    }

    autoCreateStartedRef.current = true;

    void ensureTextItem().catch(() => {
      // 全局请求拦截器负责展示接口错误。
    });
  }, [ensureTextItem, item?.id]);

  const uploadEditorImage = useCallback(
    async (file: File, options?: UploadImageOptions) => {
      if (options?.signal?.aborted) {
        throw new DOMException("Upload cancelled", "AbortError");
      }

      const item = await ensureTextItem();

      if (options?.signal?.aborted) {
        throw new DOMException("Upload cancelled", "AbortError");
      }

      const mimeType = file.type || "application/octet-stream";
      const uploadTask = await mineApi.createUploadTasks({
        file_name: file.name || getFileTitle(file),
        format: "attachment",
        item_id: item.id,
        mime_type: mimeType,
        size: String(file.size),
      });

      if (options?.signal?.aborted) {
        throw new DOMException("Upload cancelled", "AbortError");
      }

      await uploadToSignedUrl(uploadTask.upload_url, file, mimeType, options?.onProgress);

      const confirmedUploadTask = await mineApi.confirmAttachmentUpload(uploadTask.id);

      if (options?.signal?.aborted) {
        throw new DOMException("Upload cancelled", "AbortError");
      }

      const imageUrl = confirmedUploadTask.download_url;

      if (!imageUrl) {
        throw new Error("图片上传成功，但未返回图片地址");
      }

      attachmentIdByUrlRef.current.set(imageUrl, uploadTask.id);
      return { url: imageUrl };
    },
    [ensureTextItem],
  );

  const handleSubmit = async () => {
    if (isContentLoading || contentLoadError) {
      return;
    }

    const editor = editorRef.current;

    if (!editor || editor.isEmpty) {
      toast.error("文本内容不能为空");
      return;
    }

    let textPayload: ReturnType<typeof buildEditorTextPayload>;

    try {
      textPayload = buildEditorTextPayload({
        attachmentIdByUrl: attachmentIdByUrlRef.current,
        editor,
        markdownEdited: markdownEditedRef.current,
        mode: contentMode,
        normalizedTitle,
        originalMarkdown: originalMarkdownRef.current,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "文本内容无法保存");
      return;
    }

    setIsSaving(true);
    setSubmitting?.(true);

    try {
      const existingItem = await ensureTextItem();
      const mimeType = getTextContentMimeType(contentMode);
      const textFile = new Blob([textPayload.content], { type: mimeType });
      const hashKey = await computeContentHash(textFile);
      const uploadTask = await mineApi.createUploadTasks({
        file_name: buildTextContentFileName(textPayload.title, contentMode),
        format: "item",
        item_id: existingItem.id,
        mime_type: mimeType,
        size: String(textFile.size),
      });

      await uploadToSignedUrl(uploadTask.upload_url, textFile, mimeType);

      const updatedItem = await mineApi.updateItems({
        id: existingItem.id,
        upload_task_id: uploadTask.id,
        hash_key: hashKey,
        folder_id: selectedFolderId ?? undefined,
        note: textPayload.note,
        tags: {
          tag_ids: tags.map((tag) => tag.id),
        },
        text: {
          words_count: textPayload.words_count,
        },
        title: textPayload.title,
      });

      currentItemRef.current = updatedItem;
      if (contentMode === "markdown") {
        originalMarkdownRef.current = textPayload.content;
        markdownEditedRef.current = false;
      }
      await queryClient.invalidateQueries({ queryKey: mineQueryKeys.items });

      toast.success("文本保存成功");
      onCreated?.({
        action: item?.id ? "updated" : "created",
        folderId: selectedFolderId,
      });
    } catch {
      // 全局请求拦截器负责展示接口错误，这里只收束提交状态。
    } finally {
      setIsSaving(false);
      setSubmitting?.(false);
    }
  };

  const isSubmitting = isSaving;
  const isSaveDisabled = isSaving || isContentLoading || Boolean(contentLoadError);

  if (isContentLoading) {
    return <TextPreviewLoading />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mx-auto w-full max-w-4xl shrink-0 px-16 pt-8 pb-5">
        <textarea
          aria-label="标题"
          className="block max-h-32 min-h-[2.5rem] w-full resize-none overflow-hidden border-0 bg-transparent p-0 text-3xl font-bold leading-tight tracking-normal outline-none placeholder:text-muted-foreground/45 focus-visible:ring-0"
          onChange={(event) => setTitle(event.target.value)}
          placeholder="输入标题..."
          ref={titleRef}
          rows={1}
          value={title}
        />

        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
          <span>{createdAtLabel}</span>
          <span aria-hidden="true">•</span>
          <FolderSelector
            value={selectedFolderId}
            onChange={setSelectedFolderId}
            placeholder="未分类"
            allowEmpty={true}
            emptyLabel="未分类"
            className="[&>button]:h-7 [&>button]:w-auto [&>button]:border-0 [&>button]:bg-muted [&>button]:px-2 [&>button]:text-xs [&>button]:shadow-none [&>button_span]:whitespace-nowrap [&>div]:w-64 [&>div]:min-w-64 [&>div]:max-w-80"
          />
          <span aria-hidden="true">•</span>
          <TagSelector
            value={tags}
            onChange={setTags}
            className="contents text-xs text-foreground"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pr-1">
        <div className="mx-auto max-w-4xl pb-4 [&_.tiptap]:min-h-[360px] [&_.tiptap]:py-0 [&>div]:border-0 [&>div]:shadow-none">
          {contentLoadError ? (
            <div className="flex min-h-[360px] flex-col items-center justify-center gap-3 text-center">
              <span className="flex size-10 items-center justify-center rounded-md bg-destructive/10 text-destructive">
                <AlertTriangle className="size-5" />
              </span>
              <div>
                <p className="text-sm font-medium text-foreground">正文加载失败</p>
                <p className="mt-1 text-sm text-muted-foreground">{contentLoadError}</p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setContentLoadRevision((revision) => revision + 1)}
              >
                <RefreshCw />
                重试
              </Button>
            </div>
          ) : (
            <NotionEditor
              key={contentMode}
              contentMode={contentMode}
              initialContent={initialContent}
              onChange={handleEditorChange}
              onEditorReady={handleEditorReady}
              uploadImage={contentMode === "tiptap-json" ? uploadEditorImage : undefined}
            />
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-end gap-2 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          取消
        </Button>
        <Button type="button" onClick={() => void handleSubmit()} disabled={isSaveDisabled}>
          {isSubmitting ? <Loader2 className="animate-spin" /> : null}
          确认
        </Button>
      </div>
    </div>
  );
}

export function AddTextOverlay({
  initialContentMode = "tiptap-json",
  item,
  open,
  onOpenChange,
  routeInfo,
}: AddTextOverlayProps) {
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) {
        onOpenChange(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onOpenChange, open, submitting]);

  if (!open || typeof document === "undefined") {
    return null;
  }

  const handleClose = () => {
    if (!submitting) {
      onOpenChange(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 isolate z-[45] bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs">
      <div
        aria-modal="true"
        className="fixed left-1/2 top-1/2 flex h-[min(92vh,1120px)] w-[min(calc(100vw-2rem),920px)] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-hidden rounded-lg border bg-background p-6 shadow-[0_10px_15px_-3px_rgb(0_0_0_/_0.10),0_4px_6px_-4px_rgb(0_0_0_/_0.10),0_24px_80px_-24px_rgb(0_0_0_/_0.22)] duration-200 outline-none"
        role="dialog"
      >
        <Button
          aria-label="关闭"
          className="absolute right-2 top-2 opacity-70 hover:opacity-100"
          disabled={submitting}
          onClick={handleClose}
          size="icon-sm"
          type="button"
          variant="ghost"
        >
          <X className="size-4" />
        </Button>

        <AddText
          initialContentMode={initialContentMode}
          item={item}
          routeInfo={routeInfo}
          setSubmitting={setSubmitting}
          onCancel={handleClose}
          onCreated={() => onOpenChange(false)}
        />
      </div>
    </div>,
    document.body,
  );
}
